/**
 * captureSpec — spécification déterministe de la capture par section (US-VDPI-05).
 *
 * Pilier #1 (économie tokens) : TOUT est Python/TS pur, ZÉRO appel LLM.
 *   - mode de capture par section (guidé / dictée / confirmation) — NQ-C, plan §6
 *   - séquence des champs guidés (constantes, score)
 *   - readback (relecture AC4) déterministe par section
 *   - mise en forme `data` au commit (cohérente avec la section)
 *   - garde-fous : bornes critiques constantes (#13), complétude score (#8),
 *     refus jamais « réalisé » (#15)
 *
 * Module pur, sans dépendance DOM ni réseau : testable en isolation.
 * Réf : docs/meta/plan/PLAN-VOCAL-DPI-JSON-2026-06-17.md §6 §7.2.
 */

import type { MenuItem, PatientLettre } from '@/shared/types/dpi';

/** Mode de capture (NQ-C). */
export type CaptureMode = 'guided' | 'dictation' | 'confirm';

/** Définition d'un champ guidé. */
export interface FieldSpec {
  /** Clé technique stockée dans `data`. */
  key: string;
  /** Question posée à voix haute (« Tension ? »). */
  prompt: string;
}

/** Séquence de champs guidés par section (constantes / score). */
const GUIDED_FIELDS: Record<string, FieldSpec[]> = {
  constantes: [
    { key: 'tension', prompt: 'Tension ?' },
    { key: 'pouls', prompt: 'Pouls ?' },
    { key: 'temperature', prompt: 'Température ?' },
    { key: 'saturation', prompt: 'Saturation ?' },
  ],
  // Algoplus = 5 items (#8) — complétude obligatoire, pas de score partiel.
  score: [
    { key: 'visage', prompt: 'Visage ?' },
    { key: 'regard', prompt: 'Regard ?' },
    { key: 'plaintes', prompt: 'Plaintes ?' },
    { key: 'corps', prompt: 'Corps ?' },
    { key: 'comportement', prompt: 'Comportement ?' },
  ],
};

/** Mode de capture déduit de la section (plan §6). */
export function captureModeFor(section: string): CaptureMode {
  if (section === 'constantes' || section === 'score') return 'guided';
  if (section === 'administration') return 'confirm';
  // transmission / refus / surveillance / lecture → dictée courte narrative
  return 'dictation';
}

/** Champs guidés d'une section (vide si non guidé). */
export function guidedFields(section: string): FieldSpec[] {
  return GUIDED_FIELDS[section] ?? [];
}

// ─── Garde-fous (plan §6) ──────────────────────────────────────────────────────

/** Extrait le 1er nombre d'une réponse vocale (« 12 7 » → 12, « 2,5 » → 2.5). */
function firstNumber(value: string): number | null {
  const m = value.replace(',', '.').match(/-?\d+(?:\.\d+)?/);
  return m ? Number(m[0]) : null;
}

/** Bornes critiques constantes (#13 glycémie) → message d'alerte vocale ou null. */
export function constanteAlert(key: string, value: string): string | null {
  const n = firstNumber(value);
  if (n === null) return null;
  switch (key) {
    case 'glycemie':
      // g/L : hypoglycémie < 0.7, hyperglycémie sévère > 3 (cas #13).
      if (n < 0.7) return `Alerte : glycémie basse à ${n}. Vérifiez le patient.`;
      if (n > 3) return `Alerte : glycémie élevée à ${n}. Surveillance requise.`;
      return null;
    case 'temperature':
      if (n >= 38.5) return `Alerte : fièvre à ${n} degrés.`;
      if (n < 35) return `Alerte : hypothermie à ${n} degrés.`;
      return null;
    case 'saturation':
      if (n < 90) return `Alerte : saturation basse à ${n} pour cent.`;
      return null;
    case 'pouls':
      if (n > 120) return `Alerte : tachycardie à ${n}.`;
      if (n < 40) return `Alerte : bradycardie à ${n}.`;
      return null;
    default:
      return null;
  }
}

/**
 * Complétude d'un score (#8 Algoplus) : renvoie le 1er champ manquant (prompt)
 * ou null si complet. Pas de score partiel.
 */
export function missingField(section: string, data: Record<string, string>): FieldSpec | null {
  for (const f of guidedFields(section)) {
    const v = data[f.key];
    if (v === undefined || v.trim() === '') return f;
  }
  return null;
}

// ─── Concordance d'identité (AC5 US-VDPI-04, scénario #10) ──────────────────────

/** Normalise un libellé pour comparaison (minuscule, sans accent). */
function norm(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/**
 * Détecte un conflit de concordance : la capture (texte dicté) nomme un patient
 * de la chambre DIFFÉRENT du dossier ouvert → refus d'écriture (pas de lien
 * hasardeux). Déterministe, zéro LLM.
 *
 * Retourne le nom du patient en conflit, ou null si aucun conflit.
 */
export function concordanceConflict(
  text: string,
  patient: PatientLettre,
  others: PatientLettre[],
): string | null {
  const t = norm(text);
  for (const o of others) {
    if (o.patient_id === patient.patient_id) continue;
    if (t.includes(norm(o.nom))) return o.nom;
  }
  return null;
}

// ─── Mise en forme `data` au commit (cohérente avec la section) ─────────────────

/**
 * Construit le `data` envoyé à POST /saisie selon la section.
 *   - administration → { medicament, dose, voie, prescription_id, realise: true }
 *   - constantes / score → { valeurs: {champ: valeur}, ... } + score calculé
 *   - refus → { motif, realise: false }  (#15 : jamais marqué réalisé)
 *   - transmission / autre → { texte }
 */
export function buildData(
  section: string,
  item: MenuItem,
  fields: Record<string, string>,
  text: string,
): Record<string, unknown> {
  switch (section) {
    case 'administration':
      return {
        medicament: item.medicament ?? null,
        dose: item.dose ?? null,
        voie: item.voie ?? null,
        prescription_id: item.prescription_id ?? null,
        realise: true,
      };
    case 'constantes':
      return { valeurs: { ...fields } };
    case 'score': {
      // Score numérique sommé si les valeurs sont des nombres (Algoplus /5).
      const total = guidedFields(section).reduce((acc, f) => {
        const n = firstNumber(fields[f.key] ?? '');
        return acc + (n ?? 0);
      }, 0);
      return { items: { ...fields }, score: total, complet: true };
    }
    case 'refus':
      return { motif: text, realise: false };
    default:
      return { texte: text };
  }
}

// ─── Readback déterministe (relecture AC4, ZÉRO LLM — pilier #1) ─────────────────

/** Relecture vocale de la capture avant écriture. */
export function readback(
  section: string,
  item: MenuItem,
  fields: Record<string, string>,
  text: string,
): string {
  switch (section) {
    case 'administration':
      return `Confirmation : ${item.medicament ?? 'médicament'} ${item.dose ?? ''}`.trim() + '. Administré ?';
    case 'constantes': {
      const parts = guidedFields(section)
        .map((f) => `${f.key} ${fields[f.key] ?? ''}`.trim())
        .filter((p) => p.split(' ').length > 1);
      return `Relecture constantes : ${parts.join(', ')}.`;
    }
    case 'score': {
      const parts = guidedFields(section)
        .map((f) => `${f.key} ${fields[f.key] ?? ''}`.trim())
        .filter((p) => p.split(' ').length > 1);
      return `Relecture score : ${parts.join(', ')}.`;
    }
    case 'refus':
      return `Refus de soin noté : ${text}. Non réalisé.`;
    default:
      return `J'ai noté : ${text}.`;
  }
}
